import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ArticleCard from '../components/ArticleCard';
import DigestArticleCard from '../components/DigestArticleCard';
import { digestsAPI, configAPI, preferencesAPI } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, FileText, AlertCircle, Play, Plus } from 'lucide-react';

const DigestsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { digestId } = useParams();
  const multiDigestProfilesEnabled = useFeatureFlag('enable_multi_digest_profiles');
  
  const [loading, setLoading] = useState(true);
  const [digests, setDigests] = useState([]);
  const [selectedDigest, setSelectedDigest] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [specialtyConfig, setSpecialtyConfig] = useState(null);
  const [runningDigest, setRunningDigest] = useState(false);
  const [profileCount, setProfileCount] = useState(0);
  const [maxProfiles, setMaxProfiles] = useState(5);

  const handleRunDigestNow = async () => {
    try {
      setRunningDigest(true);
      const result = await digestsAPI.runDigestNow(false);
      if (result.article_count > 0) {
        toast.success(`Digest generated with ${result.article_count} new articles!`);
        await loadData();
      } else {
        toast.info('No new articles since your last digest.');
      }
    } catch (error) {
      console.error('Error running digest:', error);
      toast.error(error.response?.data?.detail || 'Failed to run digest');
    } finally {
      setRunningDigest(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (digestId && digests.length > 0) {
      loadDigestDetail(digestId);
    }
  }, [digestId, digests]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load config and preferences
      const config = await configAPI.getSpecialties();
      setSpecialtyConfig(config);
      
      try {
        const prefs = await preferencesAPI.getMyPreferences();
        setPreferences(prefs);
      } catch (error) {
        console.log('No preferences found');
      }
      
      // Load digests
      const digestResponse = await digestsAPI.getMyDigests();
      setDigests(digestResponse?.digests || []);

      // Load digest profiles count (only if feature is enabled)
      if (multiDigestProfilesEnabled) {
        try {
          const profilesData = await preferencesAPI.listProfiles();
          setProfileCount(profilesData?.profiles?.length || 0);
          setMaxProfiles(profilesData?.max_profiles || 5);
        } catch (err) {
          console.log('Could not load profiles');
        }
      }
    } catch (error) {
      console.error('Error loading digests:', error);
      toast.error('Failed to load digests');
    } finally {
      setLoading(false);
    }
  };

  const loadDigestDetail = async (id) => {
    try {
      setLoadingDetail(true);
      const detail = await digestsAPI.getDigestDetails(id);
      setSelectedDigest(detail);
    } catch (error) {
      console.error('Error loading digest detail:', error);
      toast.error('Failed to load digest details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-green-100 text-green-800',
      'sent': 'bg-blue-100 text-blue-800',
      'failed': 'bg-red-100 text-red-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'skipped_unverified': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading digests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-slate-50 to-white">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-sm border-b border-sky-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/home')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Digest History</h1>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <p className="text-gray-600">
                {digests.length} digest{digests.length !== 1 ? 's' : ''} generated
              </p>
              {profileCount > 0 && (
                <Badge
                  className={`text-sm px-3 py-1 ${profileCount < maxProfiles ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                  variant="outline"
                >
                  {profileCount} / {maxProfiles} Digest Profiles
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {profileCount < maxProfiles && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/preferences')}
                  className="border-sky-200 text-sky-700 hover:bg-sky-50"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Create Another Digest
                </Button>
              )}
              <Button
                onClick={handleRunDigestNow}
                disabled={runningDigest}
                className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white"
              >
                <Play className="h-4 w-4 mr-2" />
                {runningDigest ? 'Running...' : 'Run Digest Now'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* AI Disclaimer */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-900">
              <strong>AI-Generated Content:</strong> All summaries are AI-generated based on article abstracts for educational purposes only. Always review full articles and primary sources before making clinical decisions.
            </div>
          </div>
        </div>

        {digests.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Digest List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">All Digests</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y max-h-[600px] overflow-y-auto">
                    {digests.map((digest) => (
                      <div
                        key={digest.digest_id}
                        onClick={() => {
                          setSelectedDigest(null);
                          navigate(`/digests/${digest.digest_id}`);
                        }}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedDigest?.digest_id === digest.digest_id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {formatDate(digest.generated_at)}
                            </span>
                          </div>
                          <Badge className={getStatusColor(digest.status)}>
                            {digest.status}
                          </Badge>
                        </div>
                        {(digest.specialty_name || digest.subspecialty_name) && (
                          <div className="text-xs text-sky-700 font-medium mb-1">
                            {[digest.specialty_name, digest.subspecialty_name].filter(Boolean).join(' / ')}
                          </div>
                        )}
                        <div className="text-sm text-gray-600">
                          {digest.article_count || 0} articles
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Digest Detail */}
            <div className="lg:col-span-2">
              {loadingDetail ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading digest...</p>
                  </CardContent>
                </Card>
              ) : selectedDigest ? (
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>Digest Details</CardTitle>
                          <p className="text-sm text-gray-600 mt-1">
                            Generated {formatDate(selectedDigest.generated_at)}
                          </p>
                        </div>
                        <Badge className={getStatusColor(selectedDigest.status)}>
                          {selectedDigest.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          {selectedDigest.article_count || 0} articles in this digest
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {selectedDigest.articles && selectedDigest.articles.length > 0 ? (
                    <div className="space-y-3">
                      {selectedDigest.articles.map((article, idx) => (
                        <DigestArticleCard
                          key={article.pmid || idx}
                          article={article}
                          index={idx}
                          onAction={(pmid, action) => {
                            // Optional: could track screening progress here
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No articles in this digest</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Digest</h3>
                    <p className="text-gray-600">Click on a digest to view its articles</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Digests Yet</h3>
              <p className="text-gray-600 mb-4">
                Run your first digest to start receiving personalized literature updates
              </p>
              <Button onClick={() => navigate('/home')}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DigestsPage;